import { gql, useQuery } from '@apollo/client';
import { useCurrentUser } from 'libs/hooks';
import { get } from 'lodash';
import React from 'react';
import badReleaseCodes from 'libs/badReleaseCodes';
import copyToClipboard from 'libs/copyToClipboard';
import titleCase from 'libs/titleCase';
import { InfoCircleOutlined, LinkOutlined } from '@ant-design/icons';
import { Tag, Tooltip } from 'antd';
import type { UsConsumptionEntry } from '@xbcb/api-gateway-client';
import { AccountType, RecordType, TagKey } from '@xbcb/shared-types';
import { getReleaseCodeNames } from '@xbcb/ui-utils';
import { getBadgeIcons } from 'components/AppRecordBadge';
import WorkOrderCensusWarning from 'components/WorkOrderCensusWarning';
import MOTIcon from 'components/MOTIcon';
import { getWorkOrderBadges } from 'libs/getRecordBadges';
import { AppRecordSecondaryHeaderContentProps } from 'routes';
import { StyledDiv, StyledWarningOutline } from './styles';
import { Link } from 'react-router-dom';
import pluralize from 'pluralize';
import { paramCase } from 'change-case';
import { getOneQueryLite, getRecordFromResponse } from '@xbcb/shared-queries';
import { isProject42Enabled, isIdpFeatureEnabled } from 'featureFlags';
import { UsCbpEntryReleaseImmediateDelivery } from '@xbcb/work-order-types';
import { GetShipmentStatusDocument } from '__generated__/graphql';
import ShipmentStatus from 'components/ShipmentStatus';

gql`
  query GetShipmentStatus($id: ID!) {
    shipment(id: $id) {
      legs {
        commercialInvoiceDetail {
          status
        }
      }
    }
  }
`;

const UsConsumptionEntrySecondaryHeader: React.FC<
  AppRecordSecondaryHeaderContentProps
> = ({ record }) => {
  const { account, accountType } = useCurrentUser();
  const isP42 = isProject42Enabled({ shipperId: account?.id });

  const shipmentId = record?.group?.shipment?.id;
  const { data: shipmentData } = useQuery(GetShipmentStatusDocument, {
    variables: { id: shipmentId },
    skip: !shipmentId,
  });
  const shipmentStatus =
    shipmentData?.shipment?.legs?.[0]?.commercialInvoiceDetail?.status;

  const releaseCodeNames = getReleaseCodeNames();
  const motTitle = titleCase(record?.conveyance?.modeOfTransport);
  const releaseIssues =
    record?.releaseDispositions &&
    record?.releaseDispositions?.filter((x: { code: string }) =>
      badReleaseCodes.includes(x.code),
    );
  const badgeIcons = getBadgeIcons(record, getWorkOrderBadges);

  const originalId = (record as UsConsumptionEntry)?.tags?.find(
    (tag) => tag.key === TagKey.CLONED_FROM_WO_ID,
  )?.value;
  const entryNumberResponse = useQuery(
    getOneQueryLite({
      recordName: RecordType.US_CONSUMPTION_ENTRY,
      fields: 'entryNumber',
      queryName: 'getEntryNumber',
    }),
    {
      variables: {
        id: originalId,
      },
      skip: !originalId,
    },
  );
  const OriginalEntryTag = () => {
    return originalId && !entryNumberResponse.loading ? (
      <Tooltip title="Go to Original Entry">
        <Tag key="original-entry">
          <Link
            to={`/${pluralize(
              paramCase(RecordType.US_CONSUMPTION_ENTRY),
            )}/${originalId}`}
          >
            {`Replacement for Entry Number: ${
              getRecordFromResponse(
                entryNumberResponse,
                'get',
                RecordType.US_CONSUMPTION_ENTRY,
              ).entryNumber
            }`}
          </Link>
        </Tag>
      </Tooltip>
    ) : null;
  };

  const ImmediateDeliveryIndicator = ({ enabled }: { enabled: boolean }) => {
    if (!enabled) return null;

    return (
      <Tooltip
        title="Submitted under the immediate delivery procedure"
        placement="top"
      >
        <Tag>IDP Enabled</Tag>
      </Tooltip>
    );
  };

  if (isP42) return null;

  return (
    <StyledDiv>
      <Tooltip title="Copy the Entry URL" placement="top">
        <LinkOutlined onClick={() => copyToClipboard(window.location.href)} />
      </Tooltip>
      <Tooltip title={motTitle} placement="top">
        <span>
          <MOTIcon mot={record?.conveyance?.modeOfTransport} size="18" />
        </span>
      </Tooltip>
      {badgeIcons}

      <ShipmentStatus status={shipmentStatus} />
      {accountType === AccountType.OPERATOR &&
        !record?.deleted &&
        ['DELETED', 'REJECTED'].includes(record?.summaryStatus) && (
          <Tooltip
            title={<div>Entry Summary currently DELETED</div>}
            placement="top"
          >
            <StyledWarningOutline />
          </Tooltip>
        )}
      {accountType === AccountType.OPERATOR &&
        record?.deleted &&
        record?.summaryStatus === 'FILED' && (
          <Tooltip
            title={<div>Entry Summary currently FILED, should be DELETED</div>}
            placement="top"
          >
            <StyledWarningOutline />
          </Tooltip>
        )}
      {accountType === AccountType.OPERATOR &&
        record?.deleted &&
        record?.releaseStatus !== 'DELETED' && (
          <Tooltip
            title={
              <div>
                Entry Release is {record?.releaseStatus}, should be DELETED
              </div>
            }
            placement="top"
          >
            <StyledWarningOutline />
          </Tooltip>
        )}
      {accountType === AccountType.OPERATOR &&
        record?.summaryRejectionReasons && (
          <Tooltip
            title={
              <div>
                <p className="space-bottom">Entry Summary (AE/AX) rejected:</p>
                {record?.summaryRejectionReasons.map(
                  (r: { name: string; code: string }, i: number) => (
                    <p key={i}>{r.name}</p>
                  ),
                )}
              </div>
            }
            placement="top"
          >
            <StyledWarningOutline />
          </Tooltip>
        )}
      {accountType === AccountType.OPERATOR &&
        record?.releaseRejectionReasons && (
          <Tooltip
            title={
              <div>
                <p className="space-bottom">Entry Release (SE/SX) rejected:</p>
                {record?.releaseRejectionReasons.map(
                  (r: { name: string; code: string }, i: number) => (
                    <p key={i}>{r.name}</p>
                  ),
                )}
              </div>
            }
            placement="top"
          >
            <StyledWarningOutline />
          </Tooltip>
        )}
      {accountType === AccountType.OPERATOR &&
      releaseIssues &&
      releaseIssues.length ? (
        <Tooltip
          title={
            <div>
              <p className="space-bottom">Entry Release (SO) issues:</p>
              {record?.releaseDispositions.map(
                (r: { billNumber?: string; code: string }, i: number) => (
                  <p key={i}>
                    {r.billNumber ? `${r.billNumber} ` : ''}
                    {get(releaseCodeNames, r.code, r.code)}
                  </p>
                ),
              )}
            </div>
          }
          placement="top"
        >
          <StyledWarningOutline />
        </Tooltip>
      ) : null}
      <WorkOrderCensusWarning record={record as UsConsumptionEntry} />
      {record?.pgaDispositions?.length ? (
        <Tooltip
          title={
            <div>
              <p className="space-bottom">PGA release status:</p>
              {record?.pgaDispositions?.map(
                ({ code, status }: { code: string; status: string }) => (
                  <p key={code}>
                    {code}: {status}
                  </p>
                ),
              )}
            </div>
          }
        >
          <InfoCircleOutlined className="pga-release-icon" />
        </Tooltip>
      ) : null}
      <ImmediateDeliveryIndicator
        enabled={
          isIdpFeatureEnabled &&
          record?.immediateDeliveryIndicator ===
            UsCbpEntryReleaseImmediateDelivery.ENABLED
        }
      />
      <span>
        <OriginalEntryTag />
      </span>
    </StyledDiv>
  );
};

export default UsConsumptionEntrySecondaryHeader;
